import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePopup } from '../../contexts/PopupContext';
import SingleEvent from '../../classes/events/SingleEvent';
import { resolveAddress } from '../../utils/geocodeEdge';
import { isEventNotFinishedYet } from '../../utils/eventSchedule';
import { useGeocoding } from '../discover/map/hooks';
import { ProfileCardMapPinIcon } from '../profile/ProfileActivityIcons';
import EventMap from './EventMap';
import './EventInfoSection.css';

function extractPostalCode(text) {
  return String(text || '').match(/\b\d{2}-\d{3}\b/)?.[0] || null;
}

function EventInfoDateIcon() {
  return (
    <svg className="event-info-card-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <rect x="4" y="5.5" width="16" height="14" rx="3.2" />
      <path d="M8 3.8v3.4M16 3.8v3.4M4.8 9.4h14.4" />
      <path d="M8.2 13h.01M12 13h.01M15.8 13h.01M8.2 16.2h.01M12 16.2h.01" />
    </svg>
  );
}

function EventInfoTimeIcon() {
  return (
    <svg className="event-info-card-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="8.2" />
      <path d="M12 7.7V12l3.2 2" />
      <path d="M6.2 4.8 4.4 3M17.8 4.8 19.6 3" />
    </svg>
  );
}

function EventInfoPriceIcon() {
  return (
    <svg className="event-info-card-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path d="M5.2 9.2 11.8 4l7 5.2v8.1a2.7 2.7 0 0 1-2.7 2.7H7.9a2.7 2.7 0 0 1-2.7-2.7V9.2Z" />
      <path d="M9 10.4h5.1a2.1 2.1 0 0 1 0 4.2H9" />
      <path d="M9 8.5v7.9M15.2 16.4H9" />
    </svg>
  );
}

function EventInfoCopyIcon() {
  return (
    <svg className="event-info-location-copy-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <rect x="8" y="8" width="10" height="12" rx="2.2" />
      <path d="M6 16H5.8A2.8 2.8 0 0 1 3 13.2V6.8A2.8 2.8 0 0 1 5.8 4h6.4A2.8 2.8 0 0 1 15 6.8V7" />
    </svg>
  );
}

function EventInfoSection({
  event,
  host,
  participantsCount,
  isHost,
  isParticipant = false,
  currentUser,
  hasInvitation,
  onEventCancelled,
  onEventUpdated,
}) {
  const { t } = useTranslation(['events', 'shared']);
  const { openPopup } = usePopup();
  const [coordinates, setCoordinates] = useState(null);
  const [resolvedLocationAddress, setResolvedLocationAddress] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [locationCopied, setLocationCopied] = useState(false);
  const { geocodeSingleEvent } = useGeocoding();

  // Create SingleEvent instance for formatting and helper methods
  const eventInstance = new SingleEvent(event.id, event);
  const daysUntil = eventInstance.getDaysUntilEvent();
  const eventStatus = eventInstance.getEventStatus();
  const displayStatus = eventInstance.getDisplayStatus(t);
  const duration = eventInstance.formatDuration();
  const eventIsOpenForManagement = isEventNotFinishedYet(event);
  const postalCode = extractPostalCode(event.location) || extractPostalCode(resolvedLocationAddress);
  const copyLocationText =
    postalCode && !String(event.location || '').includes(postalCode)
      ? `${event.location}, ${postalCode}`
      : event.location;

  // Calculate end time
  const calculateEndTime = () => {
    if (!event.start_time || !event.duration_minutes) return null;
    const [hours, minutes] = event.start_time.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + event.duration_minutes;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
  };
  const endTime = calculateEndTime();

  // Geocode location on mount (optional - EventMap can also do this)
  useEffect(() => {
    const geocodeLocation = async () => {
      if (!event.location) {
        return;
      }

      try {
        // Użyj hooka useGeocoding z cache'owaniem
        const geocodedEvent = await geocodeSingleEvent(event);
        
        if (geocodedEvent && geocodedEvent.coordinates) {
          setCoordinates(geocodedEvent.coordinates);
        }

        if (!extractPostalCode(event.location)) {
          const resolved = await resolveAddress(event.location);
          if (resolved?.address) {
            setResolvedLocationAddress(resolved.address);
          }
        }
      } catch (err) {
        console.warn('Error geocoding location:', err);
      }
    };

    geocodeLocation();
  }, [event, geocodeSingleEvent]);
  // Function to handle event cancellation
  const handleCancelEvent = async () => {
    if (!isHost || !currentUser) return;

    const confirmMessage = t('events.cancelEventConfirm', 'Czy na pewno chcesz anulować to wydarzenie?');
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setCancelling(true);
    try {
      const result = await SingleEvent.cancelEvent(event.id, currentUser.id, t);
      if (result.success) {
        // Notify parent component or reload page
        if (onEventCancelled) {
          onEventCancelled();
        } else {
          // If no callback, reload the page
          window.location.reload();
        }
      } else {
        alert(t('events.cancelEventError', 'Wystąpił błąd podczas anulowania wydarzenia.') + ': ' + (result.error?.message || 'Nieznany błąd'));
        setCancelling(false);
      }
    } catch (error) {
      console.error('Error cancelling event:', error);
      alert(t('events.cancelEventError', 'Wystąpił błąd podczas anulowania wydarzenia.') + ': ' + error.message);
      setCancelling(false);
    }
  };

  const copyLocation = async () => {
    if (!copyLocationText) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(copyLocationText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = copyLocationText;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setLocationCopied(true);
      window.setTimeout(() => setLocationCopied(false), 1600);
    } catch (error) {
      console.warn('Error copying event location:', error);
    }
  };


  return (
    <section className="event-info-section">
      {/* Status and Type Badges */}
      <div className="event-info-badges">
        {/* Event Status Badge */}
        <div className="event-info-status-badge">
          <span className={`event-info-status-badge-icon event-info-status-${eventStatus}`}>
            {eventStatus === 'in_progress' ? '🟢' : eventStatus === 'ended' ? '🔴' : eventInstance.isStartingSoon() ? '🟡' : '🔵'}
          </span>
          <span className="event-info-status-badge-text">{displayStatus}</span>
        </div>
      </div>

      {/* Description */}
      {event.description && (
        <div className="event-info-description-card">
          <h3 className="event-info-description-title">{t('events.info.description')}</h3>
          <p className="event-info-description-text">{event.description}</p>
        </div>
      )}

      {(isHost || isParticipant) && event.additional_instructions?.trim() && (
        <div className="event-info-description-card event-info-instructions-card">
          <h3 className="event-info-description-title">{t('events.info.additionalInstructions')}</h3>
          <p className="event-info-description-text">{event.additional_instructions.trim()}</p>
        </div>
      )}

      <div className="event-info-spotlight">
        <div className="event-info-key-column">
          {/* Event Details Cards */}
          <div className="event-info-cards">
            <div className="event-info-card event-info-card-date">
              <div className="event-info-card-icon"><EventInfoDateIcon /></div>
              <div className="event-info-card-content">
                <div className="event-info-card-kicker">{t('events.createEvent.date')}</div>
                <div className="event-info-card-value">{eventInstance.formatDate()}</div>
                <div className="event-info-card-label">{eventInstance.getDayName()}</div>
              </div>
            </div>

            <div className="event-info-card event-info-card-time">
              <div className="event-info-card-icon"><EventInfoTimeIcon /></div>
              <div className="event-info-card-content">
                <div className="event-info-card-kicker">{t('events.createEvent.startTime')}</div>
                <div className="event-info-card-value">
                  {eventInstance.formatTime()}{endTime && ` - ${endTime}`}
                </div>
                <div className="event-info-card-label">{duration}</div>
              </div>
            </div>

            <div className="event-info-card event-info-card-price">
              <div className="event-info-card-icon"><EventInfoPriceIcon /></div>
              <div className="event-info-card-content">
                <div className="event-info-card-kicker">{t('events.info.price')}</div>
                <div className="event-info-card-value">
                  {event.is_free 
                    ? t('events.createEvent.isFree') 
                    : event.total_cost && event.max_participants 
                      ? `${(parseFloat(event.total_cost) / event.max_participants).toFixed(2)} zł`
                      : '-'}
                </div>
                <div className="event-info-card-label">{t('events.info.price')}</div>
              </div>
            </div>
          </div>

          {/* Countdown */}
          {daysUntil > 0 && (
            <div className="event-info-countdown">
              <span className="event-info-countdown-label">{t('events.info.startsIn')}:</span>
              <span className="event-info-countdown-badge">
                {t('events.info.inDays', { count: daysUntil })}
              </span>
            </div>
          )}
        </div>

        {/* Location */}
        <div className="event-info-location-card event-info-map-column">
          <div className="event-info-location-header">
            <span className="event-info-location-icon" aria-hidden="true">
              <ProfileCardMapPinIcon size={26} className="event-info-location-icon-svg" />
            </span>
            <div className="event-info-location-copy-block">
              <span className="event-info-location-eyebrow">{t('events.info.location', 'Adres wydarzenia')}</span>
              <span className="event-info-location-address">{copyLocationText}</span>
            </div>
            <button
              type="button"
              className={`event-info-location-copy ${locationCopied ? 'event-info-location-copy--copied' : ''}`}
              onClick={copyLocation}
              title={t('events.info.copyLocationTooltip', 'Kliknij, aby skopiować adres wydarzenia')}
              aria-label={t('events.info.copyLocationTooltip', 'Kliknij, aby skopiować adres wydarzenia')}
            >
              <EventInfoCopyIcon />
              <span>{locationCopied ? t('events.info.locationCopied') : t('events.info.copyLocation')}</span>
            </button>
          </div>
          {/* Map with event marker */}
          <div className="event-info-location-map">
            <EventMap
              event={event}
              location={event.location}
              coordinates={coordinates}
            />
          </div>
        </div>
      </div>

      {/* Edit and Cancel Event Buttons - only for host */}
      {isHost && !event.is_cancelled && eventIsOpenForManagement && (
        <div className="event-info-host-actions">
          <button
            className="event-info-edit-button"
            onClick={() => openPopup('edit-event', { event, onSuccess: onEventUpdated })} 
            type="button"
          >
            ✏️ {t('events.editEvent.title', 'Edytuj wydarzenie')}
          </button>
          <button
            className="event-info-cancel-button"
            onClick={handleCancelEvent}
            disabled={cancelling}
            type="button"
          >
            {cancelling ? t('events.cancelling', 'Anulowanie...') : t('events.cancelEvent', 'Anuluj wydarzenie')}
          </button>
        </div>
      )}

      {/* Cancelled Badge - for all users if event is cancelled */}
      {event.is_cancelled && (
        <div className="event-info-cancelled-badge">
          <span className="event-info-cancelled-icon">⚠️</span>
          <span className="event-info-cancelled-text">{t('events.eventCancelled', 'Wydarzenie zostało anulowane')}</span>
        </div>
      )}

      {/* Event creation date - footer info */}
      {event.created_at && (
        <div className="event-info-created">
          <span className="event-info-created-label">{t('events.info.createdAt', 'Utworzono')}:</span>
          <span className="event-info-created-value">
            {new Date(event.created_at).toLocaleDateString('pl-PL', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        </div>
      )}
    </section>
  );
}

export default EventInfoSection;
